// src/components/Search/ResultsDisplay.js

import React from 'react';
import PropTypes from 'prop-types';
import { FaHeart, FaRegHeart, FaStar } from 'react-icons/fa'; // Star added for IMDb rating

export default function ResultsDisplay({
  results = [],
  selectedResult,
  onResultClick,
  server,
  favorites = [],
  onAddFavorite,
  onRemoveFavorite,
}) {
  // Helper function to generate a unique identifier for each item
  const getUniqueId = (item) => {
    return item.imdbId || item.tmdbId || item.id || item.url;
  };

  // Helper function to determine if an item is favorited
  const isFavorited = (item) => {
    const uniqueId = getUniqueId(item);
    return favorites.some((fav) => fav.id === uniqueId);
  };

  return (
    <div className="grid gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 p-4 animate-fadeInSlow">
      {results.length ? (
        results.map((result) => {
          // Generate unique identifier for the current item
          const uniqueId = getUniqueId(result);

          // Check if the current item is favorited
          const favorited = isFavorited(result);

          return (
            <div
              key={uniqueId}
              className={`relative group bg-glassOverlay rounded-lg shadow-lg hover:shadow-2xl transition-transform transform hover:scale-105 duration-200 backdrop-blur-sm cursor-pointer overflow-hidden ${
                selectedResult &&
                ((result.imdbId && selectedResult.imdbId === result.imdbId) ||
                  (result.tmdbId && selectedResult.tmdbId === result.tmdbId) ||
                  (result.url && selectedResult.url === result.url))
                  ? 'ring-4 ring-accent'
                  : ''
              }`}
              onClick={() => onResultClick(result)}
            >
              {/* IMDb Rating, top-left */}
              {result.imdbRating && result.imdbRating !== 'N/A' && (
                <div className="absolute top-2 left-2 bg-glassCircle text-white text-xs font-semibold px-2 py-1 rounded-full shadow-lg z-10 flex items-center">
                  <FaStar className="text-yellow-400 mr-1" size={12} />
                  {result.imdbRating}
                </div>
              )}

              {/* Poster Image */}
              <img
                src={
                  result.photoUrl !== 'No Image'
                    ? result.photoUrl
                    : 'https://via.placeholder.com/140x207?text=No+Image'
                }
                alt={result.title || result.name}
                className="w-full h-64 object-cover rounded-lg"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src =
                    'https://via.placeholder.com/140x207?text=No+Image';
                }}
              />

              {/* Heart Icon */}
              <div className="absolute top-2 right-2 z-10">
                {favorited ? (
                  <FaHeart
                    className="text-red-500 hover:text-red-600 cursor-pointer"
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering onResultClick
                      onRemoveFavorite(uniqueId);
                    }}
                    aria-label="Remove from favorites"
                  />
                ) : (
                  <FaRegHeart
                    className="text-white opacity-0 group-hover:opacity-100 hover:text-red-500 cursor-pointer transition-opacity duration-200"
                    size={24}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent triggering onResultClick
                      onAddFavorite(result);
                    }}
                    aria-label="Add to favorites"
                  />
                )}
              </div>

              {/* Overlay with Details */}
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 backdrop-blur-md p-4 rounded-b-lg">
                <h2 className="text-lg font-semibold text-white">{result.title || result.name}</h2>
                <p className="text-gray-300 text-sm">
                  {result.year} • {result.type || 'Movie'}
                </p>
              </div>

              {/* Description on Hover */}
              {result.description && (
                <div className="absolute inset-0 bg-black bg-opacity-75 flex items-center justify-center text-center text-gray-100 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4">
                  <p className="line-clamp-3">
                    {result.description.length > 150
                      ? `${result.description.substring(0, 150)}...`
                      : result.description}
                  </p>
                </div>
              )}
            </div>
          );
        })
      ) : (
        <p className="text-textPrimary col-span-full">No results found.</p>
      )}
    </div>
  );
}

ResultsDisplay.propTypes = {
  results: PropTypes.array.isRequired,
  selectedResult: PropTypes.object,
  onResultClick: PropTypes.func.isRequired,
  server: PropTypes.string.isRequired,
  favorites: PropTypes.array.isRequired,
  onAddFavorite: PropTypes.func.isRequired,
  onRemoveFavorite: PropTypes.func.isRequired,
};
